import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { GreenSwitch } from "./ui/green-toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { GDRIVE_BASE_URL, qrCodesApi } from "../utils/api";
import { fetchImageWithCache } from "../utils/imageCache";
import { QRCodeDetailDrawer } from "./QRCodeDetailDrawer";
import Image from "./Image";
import FullScreenQrImageModal from "./FullScreenQrImageModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import GenerateQRModal from "./GenerateQRModal";
import { useAuth } from "../hooks/useAuth";

// Date formatting helper
function formatDateTime(input) {
  if (!input) return "";

  try {
    const date = new Date(input);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }

    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    // Format the date and time, then replace the default comma with the desired one
    return new Intl.DateTimeFormat("en-GB", options)
      .format(date)
      .replace(/, (\d{2}:\d{2})/, ", $1");
  } catch (e) {
    console.error("Error formatting date:", e);
    return String(input);
  }
}
export function QRCodes() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [qrCodes, setQrCodes] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedQRImage, setSelectedQRImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeStates, setActiveStates] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedQrCodeId, setSelectedQrCodeId] = useState(null);
  const [codeData, setCodeData] = useState(null);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [downloadTotal, setDownloadTotal] = useState(0);
  const [downloadCompleted, setDownloadCompleted] = useState(0);
  const [downloadFailed, setDownloadFailed] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const sort = "-batch_id";
  const [isGenerating, setIsGenerating] = useState(false);

  const { user } = useAuth();
  const isSuperAdmin = user?.admin_type === "superadmin";

  // Loader states for toggle and status API calls
  const [toggleLoadingIds, setToggleLoadingIds] = useState(new Set());
  const [statusLoadingIds, setStatusLoadingIds] = useState(new Set());

  // Fetch QR codes from API
  const fetchQRCodes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        setQrCodes([]);
        setTotalPages(0);
        setTotal(0);
        setLoading(false);
        return;
      }
      const response = await qrCodesApi.getAllQRCodes(
        currentPage,
        limit,
        searchTerm,
        sort
      );
      if (response.data && response.data.qrcodes && response.data.pagination) {
        const qrCodesData = response.data.qrcodes;
        const pagination = response.data.pagination;
        setQrCodes(qrCodesData);
        setTotalPages(pagination.totalPages);
        setTotal(pagination.total);

        // Set toggle states from data; use code._id as key for consistency
        const initActive = {};
        qrCodesData.forEach((code) => {
          const key = code._id ?? code.id;
          initActive[key] = code.isActive ?? false;
        });
        setActiveStates(initActive);
      } else {
        console.error("Invalid API response structure");
        setQrCodes([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching QR codes:", error);
      setQrCodes([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Refetch on dependency changes
  useEffect(() => {
    fetchQRCodes();
  }, [currentPage, searchTerm, statusFilter, limit]);

  // Update QR code active status API call and refresh
  const updateQrCodeActiveStatus = async (id, isActive) => {
    try {
      const response = await qrCodesApi.updateQrCodeActiveStatus(id, isActive);
      if (response.data) {
        fetchQRCodes();
      }
    } catch (error) {
      console.error("Error updating QR code active status:", error);
    }
  };

  const handleToggle = async (_id) => {
    setToggleLoadingIds((prev) => new Set(prev).add(_id));
    setActiveStates((prev) => ({
      ...prev,
      [_id]: !prev[_id],
    }));
    try {
      await updateQrCodeActiveStatus(_id, !activeStates[_id]);
    } finally {
      setToggleLoadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(_id);
        return newSet;
      });
    }
  };

  // Update QR code status ("Used", "Unused", "Scanned") API call and refresh
  const updateQrCodeStatus = async (id, status) => {
    setStatusLoadingIds((prev) => new Set(prev).add(id));
    try {
      const response = await qrCodesApi.updateQrCodeUsedStatus(
        id,
        status.toUpperCase()
      );
      if (response.data) {
        fetchQRCodes();
      }
    } catch (error) {
      console.error("Error updating QR code status:", error);
    } finally {
      setStatusLoadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      UNUSED: "default",
      ASSIGNED: "secondary",
      SCANNED: "secondary",
    };
    const labels = {
      UNUSED: "Unused",
      ASSIGNED: "Assigned",
      SCANNED: "Scanned",
    };
    return (
      <Badge variant={variants[status] || "secondary"}>
        {labels[status] || status}
      </Badge>
    );
  };

  const filteredCodes = (qrCodes || []).filter((code) => {
    return statusFilter === "all" || code.status === statusFilter;
  });

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleQRImageClick = (code) => {
    setSelectedQRImage(code);
    setIsImageModalOpen(true);
  };

  const handleDownloadImage = async () => {
    if (!selectedQRImage) return;
    try {
      const gdriveId =
        selectedQRImage?.gdriveId ?? selectedQRImage?.gdrive_id;
      if (!gdriveId) {
        throw new Error("Missing gdriveId");
      }

      const base64 = await fetchImageWithCache(gdriveId);
      if (!base64) {
        throw new Error("Missing image data");
      }

      const base64Data = base64.startsWith("data:")
        ? base64.split(",")[1]
        : base64;
      const byteString = atob(base64Data);
      const byteArray = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i += 1) {
        byteArray[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: "image/png" });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        selectedQRImage?.batch_id +
        selectedQRImage?.code_number +
        "_qr-code.png";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading image:", error);
      try {
        const gdriveId =
          selectedQRImage?.gdriveId ?? selectedQRImage?.gdrive_id;
        if (gdriveId) {
          window.open(`${GDRIVE_BASE_URL}${gdriveId}`, "_blank");
        }
      } catch (fallback) {
        console.error("Fallback also failed:", fallback);
      }
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedQRImage(null);
  };

  const handleDownloadCurrentPage = async () => {
    if (!filteredCodes.length || isDownloading) return;

    setIsDownloadOpen(true);
    setIsDownloading(true);
    setDownloadTotal(filteredCodes.length);
    setDownloadCompleted(0);
    setDownloadFailed(0);

    const zip = new JSZip();

    for (const code of filteredCodes) {
      try {
        const gdriveId = code?.gdriveId ?? code?.gdrive_id;
        if (!gdriveId) {
          throw new Error("Missing gdriveId");
        }

        const base64 = await fetchImageWithCache(gdriveId);
        if (!base64) {
          throw new Error("Missing image data");
        }

        const base64Data = base64.startsWith("data:")
          ? base64.split(",")[1]
          : base64;

        const byteString = atob(base64Data);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i += 1) {
          byteArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "image/png" });

        const batch = code?.batchId ?? code?.batch_id ?? "batch";
        const codeNum = code?.codeNumber ?? code?.code_number ?? "qr";
        const qrid = code?.qrid ?? gdriveId ?? codeNum;
        const fileName = `${batch}_${codeNum}_${qrid}.png`;
        zip.file(fileName, blob);
      } catch (error) {
        console.error("Error downloading QR image:", error);
        setDownloadFailed((prev) => prev + 1);
      } finally {
        setDownloadCompleted((prev) => prev + 1);
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(
        zipBlob,
        `qr-codes-page-${currentPage}-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[:T]/g, "-")}.zip`
      );
    } catch (error) {
      console.error("Error creating zip:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewDetails = (code) => {
    setSelectedQrCodeId(code?._id ?? code?.id);
    setCodeData(code);
    setIsDrawerOpen(true);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return pages;
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex justify-between items-center mr-2">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold mb-2">QR Codes</h1>
          <p className="text-sm lg:text-base text-muted-foreground">
            Manage your QR codes and their assignments
          </p>
        </div>
        <Button onClick={() => setIsGenerateModalOpen(true)} disabled={!isSuperAdmin}>
          Generate QR
        </Button>
      </div>

      {/* Search, Filter and Limit Options */}
      <Card className="shadow-sm">
        <CardContent className="pt-4 lg:pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by QR Code number or unique ID..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 text-sm lg:text-base"
              />
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Select value={statusFilter} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="UNUSED">Unused</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="SCANNED">Scanned</SelectItem>
                </SelectContent>
              </Select>

              {/* Limit selector */}
              <Select value={limit.toString()} onValueChange={handleLimitChange}>
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue placeholder="Items per page" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QR Codes Table */}
      <Card className="shadow-sm">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg lg:text-xl">
              QR Code List ({total} total)
            </CardTitle>
            <Button
              variant="default"
              size="sm"
              onClick={handleDownloadCurrentPage}
              disabled={loading || filteredCodes.length === 0 || isDownloading}
            >
              {isDownloading ? "Preparing download..." : "Download Current Page QRs"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Loading QR codes...</div>
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">
                {qrCodes.length === 0
                  ? "No QR codes found"
                  : "No QR codes match the current filter"}
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch ID</TableHead>
                      <TableHead>QR Code Number</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead>Unique ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned User</TableHead>
                      <TableHead>isActive</TableHead>
                      <TableHead>QR Image</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCodes.map((code) => (
                      <TableRow
                        key={code.gdriveId ?? code._id ?? code.id}
                        className="hover:bg-muted/50"
                      >
                        <TableCell className="font-medium text-sm">
                          {code.batchId}
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {code.codeNumber}
                        </TableCell>
                        <TableCell className="font-mono text-sm uppercase">
                          {formatDateTime(code.createdAt)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {code.qrid}
                        </TableCell>

                        <TableCell>
                          <Select
                            value={code.status}
                            onValueChange={(value) =>
                              updateQrCodeStatus(code._id ?? code.id, value)
                            }
                            disabled={statusLoadingIds.has(code._id ?? code.id)}
                          >
                            <SelectTrigger className="w-32 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="UNUSED">Unused</SelectItem>
                              <SelectItem value="ASSIGNED">Assigned</SelectItem>
                              <SelectItem value="SCANNED">Scanned</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>

                        <TableCell className="text-sm">
                          {code.user?.name || "—"}
                        </TableCell>

                        <TableCell>
                          <GreenSwitch
                            checked={!!activeStates[code._id ?? code.id]}
                            onChange={() => handleToggle(code._id ?? code.id)}
                            aria-label="Toggle active status"
                            disabled={toggleLoadingIds.has(code._id ?? code.id)}
                          />
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleQRImageClick(code)}
                            className="flex items-center gap-2"
                          >
                            <Image
                              fileId={code.gdriveId}
                              alt={"QR Code"}
                              className="w-8 h-8 object-cover rounded"
                              fallbackImage={""}
                            />
                          </Button>
                        </TableCell>

                        <TableCell className="text-sm">
                          {code.admin?.name || "—"}
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(code)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3">
                {filteredCodes.map((code) => (
                  <div
                    key={code.gdriveId ?? code._id ?? code.id}
                    className="border rounded-lg p-4 space-y-3 hover:bg-muted/50"
                    style={{ borderColor: "var(--border)" }}
                  >
                    {/* Batch ID */}
                    <div className="text-sm mb-1">
                      <span style={{ color: "var(--muted-foreground)" }}>
                        Batch ID:
                      </span>
                      <p style={{ color: "var(--foreground)" }}>
                        {code.batchId}
                      </p>
                    </div>

                    {/* Date/Time */}
                    <div className="text-sm mb-1">
                      <span style={{ color: "var(--muted-foreground)" }}>
                        Date/Time:
                      </span>
                      <p style={{ color: "var(--foreground)" }}>
                        {formatDateTime(code.createdAt)}
                      </p>
                    </div>

                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h3
                          className="font-medium truncate"
                          style={{ color: "var(--foreground)" }}
                        >
                          QR #{code.codeNumber}
                        </h3>
                        <p
                          className="text-sm font-mono truncate"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          {code.qrid}
                        </p>
                      </div>
                      <div className="ml-2">{getStatusBadge(code.status)}</div>
                    </div>

                    <div className="text-sm">
                      <span style={{ color: "var(--muted-foreground)" }}>
                        Assigned to:
                      </span>
                      <p style={{ color: "var(--foreground)" }}>
                        {code.user?.name || "No user assigned"}
                      </p>
                    </div>

                    {/* Editable status dropdown */}
                    <div className="mb-1">
                      <Select
                        value={code.status}
                        onValueChange={(value) =>
                          updateQrCodeStatus(code._id ?? code.id, value)
                        }
                        disabled={statusLoadingIds.has(code._id ?? code.id)}
                      >
                        <SelectTrigger className="w-32 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UNUSED">Unused</SelectItem>
                          <SelectItem value="USED">Used</SelectItem>
                          <SelectItem value="SCANNED">Scanned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* isActive toggle */}
                    <div className="text-sm flex items-center gap-2 mb-1">
                      <span style={{ color: "var(--muted-foreground)" }}>
                        Active:
                      </span>
                      <GreenSwitch
                        checked={!!activeStates[code._id ?? code.id]}
                        onChange={() => handleToggle(code._id ?? code.id)}
                        aria-label="Toggle active status"
                        disabled={toggleLoadingIds.has(code._id ?? code.id)}
                      />
                    </div>

                    <div className="pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewDetails(code)}
                        className="w-full flex items-center justify-center gap-2"
                      >
                        <img
                          src={`https://drive.google.com/thumbnail?id=${code.gdriveId}&sz=w100`}
                          alt="QR Code"
                          className="w-6 h-6 object-cover rounded"
                          onError={(e) => {
                            const target = e.target;
                            target.style.display = "none";
                          }}
                        />
                        View QR Code
                      </Button>
                    </div>

                    {/* Created By */}
                    <div className="text-sm">
                      <span style={{ color: "var(--muted-foreground)" }}>
                        Created by:
                      </span>
                      <p style={{ color: "var(--foreground)" }}>
                        {code.admin?.name || "—"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-6">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* QR Image Modal - Full Screen Gallery */}
      <FullScreenQrImageModal
        open={isImageModalOpen}
        onOpenChange={setIsImageModalOpen}
        image={selectedQRImage}
        onDownload={handleDownloadImage}
        onClose={closeImageModal}
      />

      <QRCodeDetailDrawer
        codeData={codeData}
        qrCodeId={selectedQrCodeId}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />

      <Dialog open={isDownloadOpen} onOpenChange={setIsDownloadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Downloading QR Codes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Downloaded {downloadCompleted} / {downloadTotal} QR Codes
            </div>
            <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-200"
                style={{
                  width: `${
                    downloadTotal
                      ? Math.round((downloadCompleted / downloadTotal) * 100)
                      : 0
                  }%`,
                }}
              />
            </div>
            {downloadFailed > 0 && (
              <div className="text-xs text-destructive">
                Failed: {downloadFailed}
              </div>
            )}
            {!isDownloading && downloadCompleted === downloadTotal && (
              <div className="text-xs text-muted-foreground">
                Zip is ready. Check your downloads folder.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <GenerateQRModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onGenerate={async (data) => {
          console.log("Generate QR with data:", data);
          try {
            setIsGenerating(true);
            const payload = {
              quantity: data.quantity,
              dotsStyle: {
                color: data.dotsStyle.color,
                type: data.dotsStyle.type,
              },
              cornerSquareStyle: {
                type: data.cornerSquare.type,
                color: data.cornerSquare.color,
              },
              backgroundStyle: {
                color: data.backgroundColor,
              },
              imageStyle: {
                crossOrigin: "anonymous",
                margin: 10,
                imageSize: 0.2,
              },
            };

            const response = await qrCodesApi.generateQrCode(payload);

            if (response.statusCode === 200) {
              console.log("QR codes generated successfully:", response.data);
              fetchQRCodes(); // Refresh the list of QR codes
              setIsGenerateModalOpen(false); // Close the modal
              // Optionally, show a success toast message
            } else {
              console.error("Error generating QR codes:", response.message);
              // Optionally, show an error toast message
            }
          } catch (error) {
            console.error("API call failed:", error);
            // Optionally, show an error toast message
          } finally {
            setIsGenerating(false);
          }
        }}
        isGenerating={isGenerating}
      />
    </div>
  );
}
